import React, {createContext, useContext, useEffect, useState, useRef} from 'react'
import {createClient} from '@supabase/supabase-js'
import posthog from 'posthog-js'
import {supabase, STAGING, isStagingOverride, setStagingOverride} from '../lib/supabase'

// Sign-ins from this allowlist of emails route to the STAGING Supabase
// project even when the binary defaults to prod. Used so App Store
// reviewers can exercise unreleased features against staging without
// any change to production.
const STAGING_ROUTED_EMAILS = new Set([
  'teesync@serf.me',
  'teesync2@serf.me',
])
// Availability auto-prompt removed - availability requests are now manual via Smart Schedule
import {deleteNotificationsByType, NOTIFICATION_TYPES} from '../services/notificationService'
import {signInWithApple as runSignInWithApple} from '../services/appleAuthService'
import { trackSignOut, trackSignUpEmailVerified } from '../services/analytics'
import { identifyUser as enchargeIdentify } from '../services/enchargeService'
import { getOnboardingCompleted, markOnboardingCompleted as markOnboardingCompletedProfile, backfillLocaleIfMissing, getUserProfile } from '../services/userProfileService'
import { fetchPlan } from '../services/planService'
import { setViewerTimezoneOverride } from '../utils/timeHelpers'
import { attemptClaim } from '../utils/guestClaim'

// Fire `sign_up_email_verified` exactly once per (user, device). AuthCallback
// also fires it on the web confirmation flow, but that path is bypassed by
// Capacitor deep-link verification and by users who land verified via a
// different surface. Doing it here on every sign-in covers those cases; the
// localStorage marker keeps it idempotent.
const VERIFIED_STORAGE_KEY = 'teesync_ph_verified_'
const trackEmailVerificationIfFirstTime = (sessionUser) => {
  if (!sessionUser?.id || !sessionUser?.email_confirmed_at) return
  const key = VERIFIED_STORAGE_KEY + sessionUser.id
  try {
    if (typeof window !== 'undefined' && window.localStorage?.getItem(key)) return
    trackSignUpEmailVerified(sessionUser.id)
    if (typeof window !== 'undefined') window.localStorage?.setItem(key, '1')
  } catch {
    trackSignUpEmailVerified(sessionUser.id)
  }
}

// Background fetch of the user_profiles row to enrich both PostHog (person
// properties) and Encharge (people attributes). Runs after the initial
// identify so first paint isn't blocked on a DB round-trip.
const enrichAnalyticsPerson = async (sessionUser) => {
  if (!sessionUser?.id) return
  try {
    const profile = await getUserProfile(sessionUser)
    // Wire the user's explicit tz preference into timeHelpers so display
    // surfaces show "your local time" against the user's chosen zone (not
    // just the browser's, which can drift via VPN, Private Relay, etc.).
    setViewerTimezoneOverride(profile?.timezone || null)
    const props = {
      home_course: profile?.home_course_name || null,
      country_code: profile?.country_code || null,
      timezone: profile?.timezone || null,
      onboarding_completed: !!profile?.onboarding_completed,
    }
    if (profile?.created_at) props.profile_created_at = profile.created_at
    if (sessionUser.created_at) props.signup_at = sessionUser.created_at
    posthog.setPersonProperties(props)

    // Mirror the same shape into Encharge so segments / journeys can target
    // any of these attributes. fire-and-forget; failures logged internally.
    enchargeIdentify(sessionUser, {
      homeCourse: profile?.home_course_name || null,
      countryCode: profile?.country_code || null,
      timezone: profile?.timezone || null,
      onboardingCompleted: !!profile?.onboarding_completed,
      ...(profile?.created_at ? { profileCreatedAt: profile.created_at } : {}),
    })
  } catch (err) {
    console.warn('[analytics] person enrichment failed:', err)
  }
}

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({children}) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)
  const [onboardingCompleted, setOnboardingCompleted] = useState(false)
  const [onboardingLoading, setOnboardingLoading] = useState(true)
  const [plan, setPlan] = useState({
    plan: 'free',
    planPeriodEnd: null,
    planSource: null,
    calendarGrandfathered: false,
    isPro: false,
  })
  const [planLoading, setPlanLoading] = useState(true)
  const previousSessionRef = useRef(null)
  const availabilityProcessedRef = useRef(false)

  // Fetch the onboarding-completed flag for the active user. Backed by
  // `public.user_profiles.onboarding_completed` (boolean). ProtectedLayout
  // sends users with `false` (the default) into /onboarding on first login.
  const refreshOnboardingState = async (currentUser) => {
    if (!currentUser) {
      setOnboardingCompleted(false)
      setOnboardingLoading(false)
      return
    }
    setOnboardingLoading(true)
    try {
      const value = await getOnboardingCompleted(currentUser)
      setOnboardingCompleted(!!value)
    } catch (err) {
      console.error('Failed to read onboarding state:', err)
      setOnboardingCompleted(false)
    } finally {
      setOnboardingLoading(false)
    }
  }

  // Refresh the cached plan/entitlement snapshot. Called on session change
  // and exposed via context so the upgrade flow can force a refetch after a
  // successful purchase without waiting for the next auth event.
  const refreshPlan = async (currentUser) => {
    if (!currentUser) {
      setPlan({
        plan: 'free',
        planPeriodEnd: null,
        planSource: null,
        calendarGrandfathered: false,
        isPro: false,
      })
      setPlanLoading(false)
      return
    }
    setPlanLoading(true)
    try {
      const snapshot = await fetchPlan(currentUser)
      setPlan(snapshot)
      return snapshot
    } catch (err) {
      console.error('Failed to read plan state:', err)
      return null
    } finally {
      setPlanLoading(false)
    }
  }

  const markOnboardingComplete = async () => {
    if (!user) return { error: 'No user' }
    const result = await markOnboardingCompletedProfile(user)
    if (!result?.error) {
      setOnboardingCompleted(true)
    }
    return result
  }

  // Availability auto-prompt removed

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      try {
        const {data: {session}} = await supabase.auth.getSession()
        setSession(session)
        setUser(session?.user ?? null)
        previousSessionRef.current = session

        // Identify user with PostHog for analytics
        if (session?.user) {
          posthog.identify(session.user.id, {
            email: session.user.email,
            name: session.user.user_metadata?.full_name || session.user.email,
          })
          enrichAnalyticsPerson(session.user)
          trackEmailVerificationIfFirstTime(session.user)
          // Redeem a stashed guest-link claim token, if any (idempotent,
          // fire-and-forget - see utils/guestClaim.js)
          attemptClaim(session.user)
        }

        // Handle initial session availability check (if user is already signed in)
        if (session?.user) {
          console.log('🔐 Initial session found, checking availability...')

          // Availability auto-prompt removed
        }

        // Load onboarding completion status for the initial session in the
        // background - don't gate auth-loading on it. The wizard route + the
        // dashboard guard both wait on `onboardingLoading` separately, so
        // there's no race.
        refreshOnboardingState(session?.user ?? null)
        refreshPlan(session?.user ?? null)

        // Backfill timezone / country_code from device locale for users who
        // onboarded before those columns existed. Idempotent and non-blocking.
        if (session?.user) {
          backfillLocaleIfMissing(session.user)
        }
      } catch (error) {
        console.error('Error getting session:', error)
      } finally {
        setLoading(false)
      }
    }

    getSession()

    // Listen for auth changes
    const {data: {subscription}} = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const previousSession = previousSessionRef.current
        const isSignIn = !previousSession && session
        const isSignOut = previousSession && !session
        const isUserSwitch = previousSession?.user?.id !== session?.user?.id
        
        console.log('🔐 Auth state change:', { event, hasSession: !!session, userId: session?.user?.id })
        
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)

        // Update PostHog identity on auth state change
        if (session?.user) {
          posthog.identify(session.user.id, {
            email: session.user.email,
            name: session.user.user_metadata?.full_name || session.user.email,
          })
          enrichAnalyticsPerson(session.user)
          trackEmailVerificationIfFirstTime(session.user)
          // Redeem a stashed guest-link claim token, if any (idempotent,
          // fire-and-forget - see utils/guestClaim.js)
          attemptClaim(session.user)
        } else {
          posthog.reset()
        }

        // Reset availability processing flag when session changes
        if (isSignOut || isUserSwitch) {
          availabilityProcessedRef.current = false;
        }

        // Availability auto-prompt removed

        // Refresh onboarding completion status whenever the session changes
        // (sign in, sign out, or user switch). Fire-and-forget so we don't
        // block the auth-state listener on a network round-trip.
        refreshOnboardingState(session?.user ?? null)
        refreshPlan(session?.user ?? null)

        if (session?.user) {
          backfillLocaleIfMissing(session.user)
        }

        // Update previous session ref
        previousSessionRef.current = session
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true)
      const {data, error} = await supabase.auth.signUp({
        email,
        password,
        options: { data: userData }
      })

      if (error) {
        console.error('Signup error:', error)
        throw error
      }

      return {data, error: null}
    } catch (error) {
      console.error('Signup error:', error)
      return {data: null, error}
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    try {
      setLoading(true)

      const normalised = (email || '').trim().toLowerCase()
      const wantStaging = STAGING_ROUTED_EMAILS.has(normalised)

      // App Store reviewer / dev path: a small allowlist of emails signs in
      // against the STAGING project even though the binary defaults to prod.
      // The first time we detect one of these emails we set the override
      // flag + sign in against a one-off staging client, then reload so the
      // rest of the app boots under the staging supabase singleton.
      if (wantStaging && !isStagingOverride()) {
        const stagingClient = createClient(STAGING.url, STAGING.anonKey, {
          auth: {
            persistSession: true,
            autoRefreshToken: true,
            // Match supabase-js's default storage key for the staging project
            // so a subsequent reload picks up the session under the same key
            // the staging-rooted module-level client will look at.
            storageKey: 'sb-iwyqmtttfdiudvnuavtm-auth-token',
          },
        })
        const {data, error} = await stagingClient.auth.signInWithPassword({
          email: normalised,
          password,
        })
        if (error) return {data: null, error}
        setStagingOverride(true)
        window.location.reload()
        return {data, error: null}
      }

      // Normal prod path (and the case where the override is already set
      // - the module-level supabase client is already pointed at staging).
      const {data, error} = await supabase.auth.signInWithPassword({
        email: normalised,
        password,
      })

      if (error) throw error

      return {data, error: null}
    } catch (error) {
      return {data: null, error}
    } finally {
      setLoading(false)
    }
  }

  const signInWithApple = async () => {
    try {
      setLoading(true)
      const result = await runSignInWithApple()
      return {data: result, error: null}
    } catch (error) {
      console.error('Apple Sign In error:', error)
      return {data: null, error}
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)

      // Track sign out event before clearing user data
      if (user?.id) {
        trackSignOut(user.id);
      }

      // Delete availability notifications before signing out
      console.log('🧹 Cleaning up availability notifications...')
      await deleteNotificationsByType(NOTIFICATION_TYPES.AVAILABILITY_REQUEST)

      // Clear any navigation tracking data from localStorage
      const navigationKey = 'teesync_navigation_tracking'
      if (localStorage.getItem(navigationKey)) {
        localStorage.removeItem(navigationKey)
        console.log('🧹 Cleared navigation tracking data')
      }

      // Sign out via Supabase
      const {error} = await supabase.auth.signOut()
      if (error) throw error

      // Clear the staging-route override (no-op if not set) so the next
      // sign-in on this device defaults to the build's env (prod for the
      // App Store build). A page reload would also be safe here but isn't
      // strictly required - the next sign-in path re-evaluates the email.
      setStagingOverride(false)
    } catch (error) {
      console.error('Error signing out:', error)
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email) => {
    try {
      const {data, error} = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) throw error

      return {data, error: null}
    } catch (error) {
      return {data: null, error}
    }
  }

  const updatePassword = async (password) => {
    try {
      const {data, error} = await supabase.auth.updateUser({
        password
      })

      if (error) throw error

      return {data, error: null}
    } catch (error) {
      return {data: null, error}
    }
  }

  const updateProfile = async (profileData) => {
    try {
      const {data, error} = await supabase.auth.updateUser({
        data: {
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          full_name: `${profileData.firstName} ${profileData.lastName}`.trim(),
        }
      })

      if (error) throw error

      return {data, error: null}
    } catch (error) {
      return {data: null, error}
    }
  }

  const value = {
    user,
    session,
    loading,
    onboardingCompleted,
    onboardingLoading,
    markOnboardingComplete,
    plan: plan.plan,
    planPeriodEnd: plan.planPeriodEnd,
    planSource: plan.planSource,
    calendarGrandfathered: plan.calendarGrandfathered,
    isPro: plan.isPro,
    planLoading,
    refreshPlan: () => refreshPlan(user),
    signUp,
    signIn,
    signInWithApple,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}